import {Component, OnInit} from "@angular/core";
import {ContactService} from "../../contact/contact.service";
import {ItsmContactModel, itsmContactModelFactory, itsmContactModelFormGroup} from "../../contact/itsm-contact.model";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {FormGroup} from "@angular/forms";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {SearchHelpers} from "../../helpers/search-helper";
import {AppSettingsService} from "../../app-settings/app-settings.service";
import {Subscription} from "rxjs/Subscription";
import {pick} from "lodash";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

export interface SelectItsmContactDialogParamsInterface {
  projectId: string;
  allowCreate: boolean;
  requireBpId: boolean;
  selectedTmcContactId: string;
  itsmContacts: ItsmContactModel[];
  contactService: ContactService;
  titleModifier?: string; // What contact are we modifying
  site?: string;
  spreadsheet?: boolean;
}

export interface SelectItsmContactDialogResultInterface {
  selectedContactId: string;
  changed: boolean;
  createdContact: ItsmContactModel;
}

@Component({
  templateUrl: "./select-itsm-contact-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class SelectItsmContactDialogComponent
  extends DialogComponent<SelectItsmContactDialogParamsInterface, SelectItsmContactDialogResultInterface>
  implements SelectItsmContactDialogParamsInterface, OnInit {
  // Inputs
  projectId: string;
  allowCreate: boolean; // Only used in form so InteliJ shows it as not in use (arg)
  requireBpId: boolean;
  selectedTmcContactId: string;
  itsmContacts: ItsmContactModel[];
  contactService: ContactService;
  titleModifier: string;
  site?: string;
  spreadsheet?: boolean;

  // Other parameters
  originalTmcContactId: string;
  itsmEmailAddressMap: any = {};
  allowedEmailAddress: boolean = false;
  itsmEmailAddressInUse: boolean = false;
  itsmEmailAddressWarning: string;
  itsmFirstNameWarning: string;
  itsmLastNameWarning: string;
  itsmContactWarning: string;
  itsmBpIdMap: any = {};
  allowedBpId: boolean = false;
  itsmBpIdWarning: string;
  itsmBpIdInUse: boolean = false;
  preFilteredItsmContacts: ItsmContactModel[];
  filteredItsmContacts: ItsmContactModel[];
  displayedItsmContacts: ItsmContactModel[];

  private subscription: Subscription;
  validEmailRegEx: RegExp = /^.*@.*\..*$/; // TODO improve me
  validBpIdRegEx: RegExp = /^.*$/; // TODO remove
  contactLocales: any = {};
  contactCountries: any = {};
  contactLanguages: any = {};
  public enhanceMS4Lookup:boolean;


  loading: boolean = false;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  emptyContact: ItsmContactModel = itsmContactModelFactory();
  contactDetail: ItsmContactModel = itsmContactModelFactory();
  expandedTmcContactId: string = "";
  createSelected: boolean = false;

  error: Error;
  contactForm: FormGroup = itsmContactModelFormGroup();

  static showContactModal(
    dialogService: DialogService,
    model: SelectItsmContactDialogParamsInterface,
  ): Promise<SelectItsmContactDialogResultInterface> {
    return dialogService.showDialog(SelectItsmContactDialogComponent, model);
  }

  searchContact(trimmedEmail, trimmedFirstName, trimmedLastName) {
    const searchEmail = trimmedEmail.toLowerCase();
    const searchFirstName = trimmedFirstName.toLowerCase();
    const searchLastName = trimmedLastName.toLowerCase();

    return `${searchFirstName}-${searchLastName}-${searchEmail}`;
  }

  checkEmailAddress(emailAddress, firstName, lastName) {
    const trimmedEmail = (emailAddress || "").trim();
    const trimmedFirstName = (firstName || "").trim();
    const trimmedLastName = (lastName || "").trim();

    if (emailAddress.toLowerCase() !== trimmedEmail.toLowerCase()) this.contactForm.controls["emailAddress"].setValue(trimmedEmail);
    if (firstName.toLowerCase() !== trimmedFirstName.toLowerCase()) this.contactForm.controls["firstName"].setValue(trimmedFirstName);
    if (lastName.toLowerCase() !== trimmedLastName.toLowerCase()) this.contactForm.controls["lastName"].setValue(trimmedLastName);
    this.itsmEmailAddressInUse = false;

    if (!trimmedEmail || !trimmedFirstName || !trimmedLastName) {
      this.itsmEmailAddressWarning = "";
      this.itsmFirstNameWarning = "";
      this.itsmLastNameWarning = "";
      this.allowedEmailAddress = false;

      return;
    }

    const searchContact = this.searchContact(trimmedEmail, trimmedFirstName, trimmedLastName);

    if (this.itsmEmailAddressMap[searchContact]) {
      this.itsmContactWarning = "ERROR - The First Name, Last Name, and Email combination is already in use for this customer";
      this.allowedEmailAddress = false;
      this.itsmEmailAddressInUse = true;
      return;
    }

    if (!trimmedEmail.match(this.validEmailRegEx)) {
      this.itsmEmailAddressWarning = "ERROR - Invalid email";
      this.allowedEmailAddress = false;
      this.itsmEmailAddressInUse = false;
      return;
    }

    this.itsmEmailAddressWarning = "";
    this.itsmFirstNameWarning = "";
    this.itsmLastNameWarning = "";
    this.itsmContactWarning = "";
    this.allowedEmailAddress = true;
  }

  checkBpId(businessPartnerId) {
    const trimmedBpId = (businessPartnerId || "").trim();
    if (businessPartnerId !== trimmedBpId) this.contactForm.controls["businessPartnerId"].setValue(trimmedBpId);
    this.itsmBpIdInUse = false;

    if (!trimmedBpId) {
      this.itsmBpIdWarning = "";
      this.allowedBpId = false;
      return;
    }

    const searchBpId = trimmedBpId.toLowerCase();
    if (this.itsmBpIdMap[searchBpId]) {
      this.itsmBpIdWarning = "ERROR - BP ID is already in use at this customer";
      this.allowedBpId = false;
      this.itsmBpIdInUse = true;
      return;
    }

    if (!trimmedBpId.match(this.validBpIdRegEx)) {
      this.itsmBpIdWarning = "ERROR - Invalid BP ID";
      this.allowedBpId = false;
      return;
    }

    this.itsmBpIdWarning = "";
    this.allowedBpId = true;
  }

  submit() {
    this.result.selectedContactId = this.selectedTmcContactId;
    this.result.changed = this.selectedTmcContactId !== this.originalTmcContactId;
    this.result.createdContact = null;
    this.closeSubscriptions();
    this.close();
  }

  clear() {
    this.result.selectedContactId = this.originalTmcContactId;
    this.result.changed = false;
    this.result.createdContact = null;
    this.closeSubscriptions();
    this.close();
  }

  @handleErrors()
  loadData() {
    this.originalTmcContactId = this.selectedTmcContactId;
    this.itsmContacts = this.itsmContacts || [];
    this.adjustContactListHelper(true);

    this.itsmEmailAddressMap = this.itsmContacts
      .map(it => `${it.firstName.trim().toLowerCase()}-${it.lastName.trim().toLowerCase()}-${it.emailAddress.trim().toLowerCase()}`)
      .filter(it => it)
      .reduce((acc, it) => { acc[it] = it; return acc; }, {});

    this.itsmBpIdMap = this.itsmContacts
      .map(it => it.businessPartnerId.trim().toLowerCase())
      .filter(it => it)
      .reduce((acc, it) => { acc[it] = it; return acc; }, {});
  }

  async ngOnInit() {
    this.loadData();
    this.enhanceMS4Lookup = await this.appSettingsService.getFeaturePermission(
      "enhanceMS4Lookup",
      this.projectId,
    );
    this.subscription = this.appSettingsService.contactLocales.subscribe((contactLocales) => {
      this.contactLocales = (contactLocales || [])
        .reduce((acc, it) => { acc[it.id] = it.description; return acc; }, {});
    });
    this.subscription = this.appSettingsService.contactCountries.subscribe((contactCountries) => {
      this.contactCountries = (contactCountries || [])
        .reduce((acc, it) => { acc[it.countryId] = it.countryName; return acc; }, {});
    });

    this.subscription = this.appSettingsService.contactLanguages.subscribe((contactLanguages) => {
      this.contactLanguages = (contactLanguages || [])
        .reduce((acc, it) => { acc[it.languageId] = it.languageName; return acc; }, {});
    });
  }

  closeSubscriptions() {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  constructor(public dialogService: DialogService, private appSettingsService: AppSettingsService) {
    super(dialogService);

    this.result = {
      selectedContactId: this.selectedTmcContactId,
      changed: false,
      createdContact: null,
    };
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.refreshContactList();
  }

  closeExpandedContactHelper() {
    this.expandedTmcContactId = ""; // Close any selections
    this.contactDetail = this.emptyContact;
  }

  refreshContactList() {
    if (this.itsmContacts) {
      this.adjustContactListHelper(true);
      this.closeExpandedContactHelper();
    }
  }

  prefilterContactList() {
    // We pass the full contact list for create cases, but need to remove those contacts from being selectable
    this.itsmContacts = this.itsmContacts || [];
    this.preFilteredItsmContacts = this.itsmContacts.filter((contact) => {
      if (this.requireBpId && !contact.businessPartnerId) return false;
      return true;
    });
  }

  private adjustContactListHelper(sortSelected = false) {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.prefilterContactList();
    this.totalCount = this.preFilteredItsmContacts.length;
    this.filteredItsmContacts = this.preFilteredItsmContacts.filter((contact) => {
      if (!contact) return false; // Not sure this can occur

      if (this.searchText) {
        const values = [
          contact.emailAddress,
          contact.businessPartnerId,
          contact.firstName,
          contact.lastName,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;
        if (searchFailures) return false;
      }

      return true;
    });

    this.maxPages = Math.ceil(this.filteredItsmContacts.length / (+this.pageSize));
    if (sortSelected) {
      this.displayedItsmContacts = [
        ...this.preFilteredItsmContacts.filter(contact => contact.id === this.selectedTmcContactId),
        ...(this.filteredItsmContacts.slice(start, end).filter(
          it => it.id !== this.selectedTmcContactId, // Prevent duplicates in detail display
        )),
      ];
    } else {
      this.displayedItsmContacts = [
        ...(this.filteredItsmContacts.slice(start, end)),
      ];
    }
  }

  toggleExpandedContact(id) {
    if (this.expandedTmcContactId === id) {
      this.closeExpandedContactHelper();
    } else {
      this.expandedTmcContactId = id;
    }
    if (this.expandedTmcContactId || !this.preFilteredItsmContacts || !this.preFilteredItsmContacts.length) {
      const matchingContact = this.preFilteredItsmContacts.find(
        it => it.id === this.expandedTmcContactId,
      );
      if (matchingContact) {
        const matchCopy = itsmContactModelFactory(matchingContact);
        // And let the user know
        this.contactDetail = matchCopy;
      } else {
        this.closeExpandedContactHelper(); // There are no matches. Clear this out
      }
    } else {
      this.contactDetail = this.emptyContact;
    }
  }

  setTmcContactIdHelper(id) {
    this.selectedTmcContactId = id;
  }

  setContact(tmcContactId) {
    if (tmcContactId) {
      if (this.preFilteredItsmContacts && this.preFilteredItsmContacts.length) {
        const matchingContact = this.preFilteredItsmContacts.find(({id}) => id === tmcContactId);
        this.setTmcContactIdHelper(matchingContact ? tmcContactId : "");
      }
    } else this.setTmcContactIdHelper("");

    this.closeExpandedContactHelper();
    this.adjustContactListHelper();
  }

  @toggleLoading()
  @handleErrors()
  async createItsmContact() {
    const values = this.contactForm.getRawValue();

    this.checkEmailAddress(values.emailAddress, values.firstName, values.lastName);
    this.checkBpId(values.businessPartnerId);

    const errors = this.allowCreate ?
      [
        this.projectId ? null : "Project not found!",
        this.itsmEmailAddressWarning,
        this.itsmContactWarning,
        values.emailAddress ? null : "Email Address is required",
        (values.businessPartnerId || !this.requireBpId) ? null : "BP ID is required",
        this.itsmBpIdWarning,
        this.itsmFirstNameWarning,
        values.firstName ? null : "First Name is required",
        this.itsmLastNameWarning,
        values.lastName ? null : "Last Name is required",
        values.phoneNumber ? null : "Phone Number is required",
        values.country ? null : this.enhanceMS4Lookup ?  "Country is required" : null,
        values.language ? null : this.enhanceMS4Lookup ? "Language is required" : null

      ].filter(it => it).join(". ") :
      "Create is not enabled for this contact";

    if (errors) {
      this.error = new Error(errors);
    } else {
      this.error = null;
      this.loading = true;

      const contact = pick(
        values,
        [
          "emailAddress",
          this.requireBpId && "businessPartnerId",
          "firstName",
          "lastName",
          "phoneNumber",
          "phoneNumber2",
          "locale",
          "country",
          "language",
          "note",
          "mailStop",
        ].filter(it => it) as string[],
      );

      const createResults = await this.contactService.createItsmContact(contact, this.projectId);
      if (createResults && createResults.itsmContactId && createResults.id) {
        this.result.selectedContactId = createResults.id;
        this.result.changed = createResults.id !== this.originalTmcContactId;
        this.result.createdContact = createResults;

        this.closeSubscriptions();
        this.close();
      } else {
        this.error = new Error("The contact returned did not have the required identification information");
      }
    }
  }

  createContactEnable() {
    this.error = null;
    this.createSelected = true;
  }

  searchContactEnable() {
    this.error = null;
    if (this.itsmEmailAddressInUse) {
      this.searchText = this.contactForm.getRawValue().emailAddress;
      this.adjustContactListHelper();
    } else if (this.itsmBpIdInUse) {
      this.searchText = this.contactForm.getRawValue().businessPartnerId;
      this.adjustContactListHelper();
    }

    this.createSelected = false;
  }
}
